/* StripeWebhookLogPanel — Admin → Services tab.
 *
 * Tails the most recent rows from the `stripe_webhook_events` collection
 * so admins can confirm Stripe → ZHELTA events are flowing AND quickly
 * spot any handler errors that landed (idempotent retries don't write
 * new rows here — we return early before insertion if already-seen).
 *
 * Backend: GET /api/admin/stripe/webhook-events?limit=&only_errors=&event_type=
 *
 * Auth: re-uses the legacy admin-password header pattern (same as
 * CompareConversionWidget) since this dashboard panel mounts in the
 * super-admin scope.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Webhook, RefreshCw, AlertTriangle, CheckCircle2, Filter, X, Bug, Activity,
} from 'lucide-react';

const API = `${(process.env.REACT_APP_API_BASE_URL || process.env.CRA_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api`;
const ADMIN_PW_KEY = 'zhelta_admin_pw';

function authHeaders() {
  let pw = '';
  try { pw = localStorage.getItem(ADMIN_PW_KEY) || ''; } catch { /* ignore */ }
  return pw ? { 'X-Admin-Password': pw } : {};
}

function fmtTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const sec = Math.floor((now - d) / 1000);
    if (sec < 60) return `${sec}s ago`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
    if (sec < 604800) return `${Math.floor(sec / 86400)}d ago`;
    return d.toISOString().slice(0, 10);
  } catch { return iso; }
}

function EventTypeBadge({ type }) {
  const tone = type?.includes('failed') || type?.includes('expired')
    ? 'bg-rose-50 text-rose-700 border-rose-200'
    : type?.includes('completed') || type?.includes('succeeded') || type?.includes('paid')
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : 'bg-sky-50 text-sky-700 border-sky-200';
  return (
    <code className={`inline-flex items-center text-[10.5px] font-mono font-semibold px-1.5 py-0.5 rounded border ${tone}`}>
      {type}
    </code>
  );
}

export default function StripeWebhookLogPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [onlyErrors, setOnlyErrors] = useState(false);
  const [filterType, setFilterType] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', '50');
      if (onlyErrors) params.set('only_errors', 'true');
      if (filterType) params.set('event_type', filterType);
      const r = await fetch(`${API}/admin/stripe/webhook-events?${params.toString()}`, {
        credentials: 'include',
        headers: authHeaders(),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setErr(e.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [onlyErrors, filterType]);

  useEffect(() => { load(); }, [load]);

  const stats = data?.stats_7d || {};
  const rows = data?.rows || [];
  const typeOptions = useMemo(() => {
    const fromStats = Object.keys(stats.by_event_type || {});
    return fromStats.length > 0 ? fromStats : [
      'checkout.session.completed',
      'checkout.session.expired',
      'payment_intent.succeeded',
      'payment_intent.payment_failed',
      'invoice.payment_succeeded',
      'invoice.payment_failed',
      'customer.subscription.updated',
      'customer.subscription.deleted',
    ];
  }, [stats.by_event_type]);

  return (
    <section
      data-testid="admin-stripe-webhook-log"
      className="rounded-2xl border border-gray-200 bg-white overflow-hidden"
    >
      <header className="px-6 py-4 flex items-center justify-between bg-[#0e0e10] text-white flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Webhook className="w-4 h-4 text-[#f5a623]" />
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#f5a623]">Stripe · Webhook log</div>
            <h3 className="text-[15px] font-bold">Live event stream from Stripe</h3>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="webhook-log-reload"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/15 text-[11px] font-bold uppercase tracking-wider disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Reload
          </button>
        </div>
      </header>

      {/* 7-day stats strip */}
      <div className="px-6 py-3 grid grid-cols-2 sm:grid-cols-4 gap-2 bg-[#fffbe9] border-b border-[#f0e3a8]">
        <Stat
          icon={<Activity className="w-3.5 h-3.5 text-[#a8871a]" />}
          label="7-day events"
          value={stats.total ?? '–'}
          testid="webhook-stat-total"
        />
        <Stat
          icon={<CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />}
          label="No-error pipeline"
          value={`${(stats.total || 0) - (stats.errors || 0)}`}
          testid="webhook-stat-ok"
        />
        <Stat
          icon={<AlertTriangle className="w-3.5 h-3.5 text-rose-700" />}
          label="Handler errors"
          value={stats.errors ?? '–'}
          tone={stats.errors > 0 ? 'error' : 'ok'}
          testid="webhook-stat-errors"
        />
        <Stat
          icon={<Bug className="w-3.5 h-3.5 text-sky-700" />}
          label="Distinct types"
          value={Object.keys(stats.by_event_type || {}).length}
          testid="webhook-stat-types"
        />
      </div>

      {/* Filter row */}
      <div className="px-6 py-3 flex items-center gap-3 flex-wrap text-[12px] border-b border-gray-100">
        <span className="inline-flex items-center gap-1 text-gray-500 font-semibold">
          <Filter className="w-3.5 h-3.5" /> Filter:
        </span>
        <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={onlyErrors}
            onChange={(e) => setOnlyErrors(e.target.checked)}
            data-testid="webhook-filter-errors-only"
            className="accent-rose-600"
          />
          <span className={onlyErrors ? 'text-rose-700 font-bold' : 'text-gray-700'}>Errors only</span>
        </label>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          data-testid="webhook-filter-type"
          className="px-2 py-1 rounded-md border border-gray-200 text-[11.5px] bg-white"
        >
          <option value="">All event types</option>
          {typeOptions.map((t) => (
            <option key={t} value={t}>{t}{stats.by_event_type?.[t] ? ` (${stats.by_event_type[t]})` : ''}</option>
          ))}
        </select>
        {(onlyErrors || filterType) && (
          <button
            type="button"
            onClick={() => { setOnlyErrors(false); setFilterType(''); }}
            data-testid="webhook-filter-clear"
            className="inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-rose-700"
          >
            <X className="w-3 h-3" /> Clear
          </button>
        )}
      </div>

      {err && (
        <div className="px-6 py-4 text-[12px] text-rose-700 bg-rose-50 border-b border-rose-200">
          <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
          Failed to load webhook events: {err}
        </div>
      )}

      {/* Event list */}
      <div className="max-h-[520px] overflow-y-auto" data-testid="webhook-log-list">
        {rows.length === 0 && !loading && !err && (
          <div className="text-center py-10 px-6 text-gray-500">
            <Webhook className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-[13px]">No webhook events recorded {onlyErrors ? 'with handler errors' : 'yet'}.</p>
            <p className="text-[11px] mt-1 text-gray-400">Stripe sends events here whenever a checkout completes, a subscription renews, or a payment fails.</p>
          </div>
        )}
        {rows.map((evt) => (
          <EventRow key={evt.event_id} evt={evt} />
        ))}
      </div>
    </section>
  );
}

function Stat({ icon, label, value, tone, testid }) {
  const toneClass = tone === 'error'
    ? 'text-rose-700'
    : tone === 'ok'
    ? 'text-emerald-700'
    : 'text-[#0e0e10]';
  return (
    <div data-testid={testid} className="flex items-center gap-2">
      <div className="shrink-0">{icon}</div>
      <div className="min-w-0">
        <div className="text-[9.5px] uppercase tracking-[0.2em] text-gray-500 truncate">{label}</div>
        <div className={`text-[14px] font-extrabold ${toneClass}`}>{value}</div>
      </div>
    </div>
  );
}

function EventRow({ evt }) {
  const hasError = !!evt.processing_error;
  return (
    <div
      data-testid={`webhook-event-${evt.event_id}`}
      className={`px-6 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50/60 transition-colors ${hasError ? 'bg-rose-50/30' : ''}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <EventTypeBadge type={evt.event_type} />
        {evt.livemode ? (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9.5px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-200">LIVE</span>
        ) : (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9.5px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200">TEST</span>
        )}
        {hasError && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9.5px] font-bold uppercase tracking-wider bg-rose-100 text-rose-800 border border-rose-200">
            <AlertTriangle className="w-2.5 h-2.5" /> Handler error
          </span>
        )}
        <span className="ml-auto text-[10.5px] text-gray-500">{fmtTime(evt.received_at)}</span>
      </div>
      <div className="mt-1 grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[11.5px]">
        <span className="text-gray-500">event_id</span>
        <code className="font-mono text-[10.5px] text-gray-700 break-all">{evt.event_id}</code>
        {evt.object_id && (
          <>
            <span className="text-gray-500">object_id</span>
            <code className="font-mono text-[10.5px] text-gray-700 break-all">{evt.object_id}</code>
          </>
        )}
        {hasError && (
          <>
            <span className="text-rose-700">error</span>
            <code className="font-mono text-[10.5px] text-rose-800 bg-rose-50 px-1.5 py-1 rounded border border-rose-200 whitespace-pre-wrap">{evt.processing_error}</code>
          </>
        )}
      </div>
    </div>
  );
}
